import { Code, Database, Globe, Image, Music, Plus, Video } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import MainLayout from '../components/layout/MainLayout'
import { API_URL } from '../config/api'

interface Agent {
  id: number
  agent_name: string
  persona: string
  bio: string
  created_at: string
  llm_type: string
  known_llm: string
  interest_area: string
  expertise: string
  level: number
  can_process_image: boolean
  can_process_audio: boolean
  can_process_video: boolean
  can_generate_code: boolean
  can_search_web: boolean
  can_analyze_data: boolean
}

const LLM_META: Record<string, { label: string; color: string; group: string }> = {
  runai_cloud: { label: 'RunAI Cloud', color: '#3b82f6', group: 'Cloud' },
  cloud_api:   { label: 'Cloud API',   color: '#10b981', group: 'Cloud' },
  cf_gateway:  { label: 'CF Gateway',  color: '#f6821f', group: 'Cloudflare' },
  cf_workers:  { label: 'CF Workers',  color: '#f6821f', group: 'Cloudflare' },
  local_llm:   { label: 'Local LLM',   color: '#f59e0b', group: 'Local' },
  petals:      { label: 'Petals P2P',  color: '#8b5cf6', group: 'P2P' },
  webllm:      { label: 'WebLLM',      color: '#06b6d4', group: 'Browser' },
}

const CAPS = [
  { key: 'can_process_image', icon: Image,    label: 'Image' },
  { key: 'can_process_audio', icon: Music,    label: 'Audio' },
  { key: 'can_process_video', icon: Video,    label: 'Video' },
  { key: 'can_generate_code', icon: Code,     label: 'Code' },
  { key: 'can_search_web',    icon: Globe,    label: 'Web' },
  { key: 'can_analyze_data',  icon: Database, label: 'Data' },
] as const

export default function MyAgents() {
  const navigate = useNavigate()
  const [agents, setAgents] = useState<Agent[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<string>('all')

  useEffect(() => { void loadAgents() }, [])

  const loadAgents = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) { setLoading(false); return }
      const res = await fetch(`${API_URL}/api/users/me/agents`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json() as Agent[] | { agents: Agent[] }
        setAgents(Array.isArray(data) ? data : (data.agents || []))
      }
    } catch (err) { console.error(err) }
    finally { setLoading(false) }
  }

  // Gruplara ayir
  const groups: Record<string, Agent[]> = {}
  agents.forEach(a => {
    const meta = LLM_META[a.llm_type]
    const g = meta?.group || 'Other'
    if (!groups[g]) {groups[g] = []}
    groups[g].push(a)
  })

  const filtered = filter === 'all' ? agents : (groups[filter] || [])
  const groupKeys = Object.keys(groups)

  return (
    <MainLayout>
      <div style={{ padding: '24px 20px' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text-primary)' }}>My Agents</div>
            <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: 2 }}>
              {agents.length} agent{agents.length !== 1 ? 's' : ''}
            </div>
          </div>
          <button onClick={() => navigate('/agents/create')}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', background: 'var(--primary-color)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
            <Plus size={14} /> New Agent
          </button>
        </div>

        {/* Filtre tabs */}
        {groupKeys.length > 1 && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
            {['all', ...groupKeys].map(g => (
              <button key={g} onClick={() => setFilter(g)}
                style={{ padding: '5px 12px', borderRadius: 20, border: '1px solid var(--border-color)', background: filter === g ? 'var(--primary-color)' : 'transparent', color: filter === g ? '#fff' : 'var(--text-secondary)', fontSize: 12, cursor: 'pointer', fontWeight: filter === g ? 600 : 400 }}>
                {g === 'all' ? `All (${agents.length})` : `${g} (${groups[g].length})`}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Loading...</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🤖</div>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>No agents yet</div>
            <div style={{ fontSize: 13, marginBottom: 20 }}>Create your first agent to get started</div>
            <button onClick={() => navigate('/agents/create')}
              style={{ padding: '10px 20px', background: 'var(--primary-color)', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              Create Agent
            </button>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '14px', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
            {filtered.map(agent => {
              const meta = LLM_META[agent.llm_type] || { label: agent.llm_type, color: '#64748b', group: 'Other' }
              const activeCaps = CAPS.filter(c => agent[c.key])
              return (
                <div key={agent.id}
                  style={{ padding: '16px', background: 'var(--card-bg)', borderRadius: '12px', border: '1px solid var(--border-color)', cursor: 'pointer', transition: 'border-color 0.15s' }}
                  onClick={() => navigate(`/agent/${agent.id}`)}
                  onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--primary-color)')}
                  onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--border-color)')}>

                  {/* Agent header */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <div style={{ width: 40, height: 40, borderRadius: '50%', background: `hsl(${(agent.id * 47) % 360}, 55%, 42%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: '#fff', fontWeight: 700, flexShrink: 0 }}>
                      {agent.agent_name.charAt(0)}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {agent.agent_name}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {agent.interest_area || agent.expertise || agent.persona}
                      </div>
                    </div>
                  </div>

                  {/* Bio */}
                  {agent.bio && (
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10, lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {agent.bio}
                    </div>
                  )}

                  {/* Badges */}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: activeCaps.length ? 8 : 0 }}>
                    <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, background: meta.color + '20', color: meta.color, fontWeight: 600 }}>
                      {meta.label}
                    </span>
                    {agent.known_llm && agent.known_llm !== 'unknown' && (
                      <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, background: 'var(--body-bg)', color: 'var(--text-secondary)', border: '1px solid var(--border-color)' }}>
                        {agent.known_llm}
                      </span>
                    )}
                    {agent.level > 1 && (
                      <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, background: '#f59e0b20', color: '#f59e0b', fontWeight: 600 }}>
                        Lv.{agent.level}
                      </span>
                    )}
                  </div>

                  {/* Capabilities */}
                  {activeCaps.length > 0 && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {activeCaps.map(c => (
                        <div key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, color: 'var(--text-muted)', padding: '2px 6px', borderRadius: 4, background: 'var(--body-bg)', border: '1px solid var(--border-color)' }}>
                          <c.icon size={9} /> {c.label}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Footer */}
                  <div style={{ display: 'flex', gap: 8, marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border-color)' }}>
                    <button onClick={e => { e.stopPropagation(); navigate(`/aicloud/app/chat?agent=${agent.id}`) }}
                      style={{ flex: 1, padding: '6px', background: 'var(--primary-color)15', color: 'var(--primary-color)', border: '1px solid var(--primary-color)30', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
                      Chat
                    </button>
                    <button onClick={e => { e.stopPropagation(); navigate(`/agent/${agent.id}`) }}
                      style={{ flex: 1, padding: '6px', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-color)', borderRadius: 6, cursor: 'pointer', fontSize: 11 }}>
                      Details
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </MainLayout>
  )
}
